import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  query, where, onSnapshot, Unsubscribe
} from 'firebase/firestore';
import { db } from '../../config/firebase';

export interface PracticeTask {
  id: string;
  title: string;
  description: string;
  dueDate: string;        // ISO date string
  dueTime?: string;       // HH:mm
  priority: 'none' | 'low' | 'medium' | 'high';
  status: 'pending' | 'in_progress' | 'completed';
  // Assignment
  assignedToId?: string;  // team member authUid or team_members doc id
  assignedToName?: string;
  assignedById: string;   // who created / assigned
  assignedByName: string;
  // Client link
  clientId?: string;
  clientName?: string;
  // Completion
  completedAt?: string;
  completionNotes?: string;
  // Meta
  practiceOwnerId: string;
  createdAt: string;
  updatedAt: string;
}

const COLLECTION = 'practice_tasks';

class FirebaseTaskService {
  /**
   * Subscribe to all tasks for a practice (real-time).
   * For admin/director: sees all tasks.
   * For individual staff: filter client-side by assignedToId.
   */
  static subscribeToTasks(
    practiceOwnerId: string,
    callback: (tasks: PracticeTask[]) => void
  ): Unsubscribe {
    // Note: no orderBy to avoid requiring a composite index — sorting is done client-side
    const q = query(
      collection(db, COLLECTION),
      where('practiceOwnerId', '==', practiceOwnerId)
    );

    return onSnapshot(q, (snapshot) => {
      const tasks: PracticeTask[] = snapshot.docs.map(d => ({
        ...d.data(),
        id: d.id,
      } as PracticeTask));
      callback(tasks);
    }, (error) => {
      console.error('[FirebaseTaskService] subscribe error:', error);
      callback([]);
    });
  }

  /**
   * Create a new task.
   */
  static async createTask(task: Omit<PracticeTask, 'id'>): Promise<string> {
    // Strip undefined values — Firestore rejects them
    const clean: Record<string, any> = {};
    for (const [k, v] of Object.entries(task)) {
      if (v !== undefined) clean[k] = v;
    }
    clean.createdAt = new Date().toISOString();
    clean.updatedAt = new Date().toISOString();

    const docRef = await addDoc(collection(db, COLLECTION), clean);
    return docRef.id;
  }

  /**
   * Update a task (partial update).
   */
  static async updateTask(taskId: string, updates: Partial<PracticeTask>): Promise<void> {
    const ref = doc(db, COLLECTION, taskId);
    // Strip undefined values — Firestore rejects them
    const clean: Record<string, any> = {};
    for (const [k, v] of Object.entries(updates)) {
      if (v !== undefined) clean[k] = v;
    }
    clean.updatedAt = new Date().toISOString();
    await updateDoc(ref, clean);
  }

  /**
   * Mark task as completed with optional notes.
   */
  static async completeTask(taskId: string, completionNotes?: string): Promise<void> {
    await this.updateTask(taskId, {
      status: 'completed',
      completedAt: new Date().toISOString(),
      completionNotes: completionNotes || '',
    });
  }

  /**
   * Reopen a completed task.
   */
  static async reopenTask(taskId: string): Promise<void> {
    await this.updateTask(taskId, {
      status: 'pending',
      completedAt: undefined,
      completionNotes: undefined,
    });
  }

  /**
   * Delete a task permanently.
   */
  static async deleteTask(taskId: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, taskId));
  }
}

export default FirebaseTaskService;
